import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Checkbox,
  Button,
  Tooltip,
  Popconfirm
} from "antd";
import "@styles/styles-Settings.css";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";
import dayjs from "dayjs";
import { useDataAccess } from "../PermissionedComponents";
import { LockOutlined } from "@ant-design/icons";

const options = [
    { label: 'GPS', value: 'gps' },
    { label: 'Wellness', value: 'wellness' },
    { label: 'Testing', value: 'testing' },
    { label: 'Forceplate', value: 'forceplate' },
    { label: 'Nutrition', value: 'nutrition' },
    { label: 'Cognition', value: 'cognition' },
    { label: 'RPE', value: 'rpe' }
  ];

const { RangePicker } = DatePicker;

export const DeleteData = () => {
  const date_format = "YYYY-MM-DD";

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [checkboxStatuses, setCheckboxStatuses] = useState({
    gps: { checked: false, disabled: true },
    wellness: { checked: false, disabled: true },
    testing: { checked: false, disabled: true },
    forceplate: { checked: false, disabled: true },
    nutrition: { checked: false, disabled: true },
    cognition: { checked: false, disabled: true },
    rpe: { checked: false, disabled: true }
  });
  const [loading, setLoading] = useState(false);

  const { ready: permissionsReady, canDelete } = useDataAccess();

  const deletableOptions = options.map((option) => ({
    ...option,
    locked: !canDelete(option.value),
  }));

  const canDeleteAnyType = deletableOptions.some(({ locked }) => !locked);

  const trainingAPI = new TrainingAPI();

  const onCheckboxChange = (checkedValues) => {
    setSelectedOptions(checkedValues);
  };

  const onDateChange = (value, dateString) => {
    setStartDate(dateString[0] === "" ? null : dateString[0]);
    setEndDate(dateString[1] === "" ? null : dateString[1]);
  };

  useEffect(() => {
    if (startDate && endDate){
        getDataTypesForDate(startDate, endDate)
    }
  }, [startDate, endDate]);


  const getDataTypesForDate = async (startDate, endDate) => {
    const newStatuses = {
      gps: { checked: false, disabled: true },
      wellness: { checked: false, disabled: true },
      testing: { checked: false, disabled: true },
      forceplate: { checked: false, disabled: true },
      nutrition: { checked: false, disabled: true },
      cognition: { checked: false, disabled: true },
      rpe: { checked: false, disabled: true }

    }

    try {
      const resp = await trainingAPI.getDataDeletionTypesForDate(
        {
          startDate,
          endDate
        })

        newStatuses.gps.disabled = !resp.gps;
        newStatuses.wellness.disabled = !resp.wellness;
        newStatuses.testing.disabled = !resp.testing;
        newStatuses.forceplate.disabled = !resp.forceplate;
        newStatuses.nutrition.disabled = !resp.nutrition;
        newStatuses.cognition.disabled = !resp.cognition;
        newStatuses.rpe.disabled = !resp.rpe;

        setCheckboxStatuses(newStatuses)
        setSelectedOptions([]);
    } catch (err) {
      console.log(err);
      message.error("Error identifying data types for the given date");

      setCheckboxStatuses(newStatuses)
      setSelectedOptions([]);
    }
  }


  const onSubmit = async () => {
    // Re-check on submit: checks made before permissions resolved read as allowed,
    // so a type ticked in that window could still be sitting in selectedOptions.
    const allowedTypes = selectedOptions.filter((value) => canDelete(value));

    if (allowedTypes.length === 0) {
      message.error("You don't have permission to delete the selected data types");
      return;
    }

    setLoading(true)

    const payload = {
        startDate,
        endDate,
        datatypes: allowedTypes
    }

    try {
      await trainingAPI.deleteDataForDate(payload)
      message.success("Data Deleted Succesfully");
    } catch (err) {
      console.log(err);
      // err is undefined when no response arrives (dropped connection).
      if (err?.status == 400){
          message.error(err.data)
      } else {
          message.error("Error deleting data")
      }
    }

    setLoading(false)    
  };

 
  if (permissionsReady && !canDeleteAnyType) {
    return (
      <div>You don&apos;t have permission to delete data for any data type.</div>
    );
  }

  const selectedLabels = deletableOptions
    .filter((option) => selectedOptions.includes(option.value))
    .map((option) => option.label)
    .join(", ");

  return (
    <>
      <div className="team-settings-input-wrap settings-first-field settings-inline-field">
        <div className="team-settings-input-label">Date range</div>
        <div>
          <DatePicker.RangePicker
            onChange={onDateChange}
            value={[
              startDate !== null ? dayjs(startDate) : null,
              endDate !== null ? dayjs(endDate) : null,
            ]}
            style={{ width: 250 }}
          />
        </div>
      </div>
      <div className="team-settings-input-wrap settings-inline-field settings-inline-field-top">
        <div className="team-settings-input-label">Data types</div>
        <Checkbox.Group
          className="settings-checkbox-list"
          value={selectedOptions}
          onChange={onCheckboxChange}
        >
                {deletableOptions.map(option => (
                <Checkbox
                    key={option.value}
                    value={option.value}
                    checked={checkboxStatuses[option.value].checked}
                    disabled={checkboxStatuses[option.value].disabled || option.locked}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {option.label}
                      {option.locked && (
                        // Explicit colour: a disabled checkbox greys its contents, and
                        // every box starts disabled until a date range is picked, so an
                        // inherited-colour icon would be indistinguishable.
                        <Tooltip title={`You don't have permission to delete ${option.label} data`}>
                          <LockOutlined style={{ marginLeft: 6, color: 'rgba(0, 0, 0, 0.65)' }} />
                        </Tooltip>
                      )}
                    </span>
                </Checkbox>
                ))}
        </Checkbox.Group>
      </div>
      <div className="team-settings-input-wrap">
        <Popconfirm
          title="Delete this data?"
          description={`${selectedLabels} data from ${dayjs(startDate).format("MMM D, YYYY")} to ${dayjs(endDate).format("MMM D, YYYY")} will be deleted. You cannot undo this.`}
          okText="Delete data"
          okButtonProps={{ danger: true }}
          onConfirm={onSubmit}
        >
          <Button
            danger
            loading={loading}
            disabled={selectedOptions.length === 0}
          >
            Delete data
          </Button>
        </Popconfirm>
      </div>
    </>
  );
};
